
import { DestroyRef, Injectable, inject, DOCUMENT } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

interface AppVersionPayload {
  version?: string;
  reload?: boolean;
}

@Injectable({ providedIn: 'root' })
export class AppVersionService {
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);

  private started = false;
  private initialVersion: string | null = null;
  private pendingReload = false;

  start() {
    if (this.started || typeof window === 'undefined') {
      return;
    }

    this.started = true;

    const routerSubscription = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => void this.checkVersion());

    const handleVisibilityChange = () => {
      if (this.document.visibilityState === 'visible') {
        void this.checkVersion();
      }
    };

    const handleFocus = () => void this.checkVersion();

    this.document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    this.destroyRef.onDestroy(() => {
      routerSubscription.unsubscribe();
      this.document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    });

    void this.checkVersion();
  }

  private async checkVersion() {
    try {
      const response = await fetch('/version.json', { cache: 'no-store' });
      if (!response.ok) {
        return;
      }

      const { version, reload }: AppVersionPayload = await response.json();
      if (!version) {
        return;
      }

      if (!this.initialVersion) {
        this.initialVersion = version;
      } else if (version !== this.initialVersion) {
        this.initialVersion = version;
        if (reload !== false) {
          this.pendingReload = true;
        }
      }
    } catch {
      return;
    }

    this.tryReload();
  }

  private tryReload() {
    if (!this.pendingReload || this.document.visibilityState === 'hidden') {
      return;
    }

    this.pendingReload = false;
    window.location.reload();
  }
}
